import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';

import AdminLayouts from './Admin/AdminLayouts';
import UserLayouts from './User/UserLayouts';
import CustomerLayouts from './Customer/CustomerLayouts';
import PaymentSuccessModule from './PaymentSuccessModule';
import BookingTracker from './BookingTracker';

const PostLoginRedirect = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const auth = window?.catalyst?.auth;
    const path = location.pathname;
    const isPaymentSuccessPath = path.startsWith('/payment-success');

    const checkAuth = async () => {
      // 1. Identify restricted paths
      const isRestrictedPath = location.pathname.startsWith('/admin') || location.pathname.startsWith('/user');

      if (isPaymentSuccessPath) {
        setLoading(false);
        return;
      }

      /**
       * 2️⃣ No SDK → protect restricted routes
       */
      if (!auth) {
        if (isRestrictedPath) {
          window.location.href = "/__catalyst/auth/login";
        } else {
          setLoading(false);
        }
        return;
      }

      try {

        const res = await auth.isUserAuthenticated();

        // Success path
        if (res?.status === 200 && res?.content) {
          const { status, role_details } = res.content;
          const role = role_details?.role_name;

          if (status !== "ACTIVE") {
            navigate('/customer');
          } else if (role === "App Administrator") {
            if (!location.pathname.startsWith('/admin')) navigate('/admin');
          } else if (role === "App User") {
            if (!location.pathname.startsWith('/user')) navigate('/user');
          } else {
            if (!location.pathname.startsWith('/customer')) navigate('/customer');
          }
        }
      } catch (err) {
        // Failure path (Not authenticated)
        if (isRestrictedPath) {
          window.location.href = "/__catalyst/auth/login";
        } else if (location.pathname === "/") {
          navigate('/customer');
        }
      } finally {
        // We add a slight delay or ensure state updates before removing loader
        setLoading(false);
      }
    };

    checkAuth();
  }, [navigate, location.pathname]);

  // Comprehensive Auth Loader
  if (loading) {
    return (
      <div style={loaderStyles.container}>
        <div style={loaderStyles.content}>
          <div style={loaderStyles.spinner}></div>
          <p style={loaderStyles.text}>Securing session...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <Routes>
      <Route path="/admin/*" element={<AdminLayouts />} />
      <Route path="/user/*" element={<UserLayouts />} />
      <Route path="/customer/*" element={<CustomerLayouts />} />
      <Route path="/search-booking" element={<BookingTracker />} />
      <Route path="/payment-success/:paymentId" element={<PaymentSuccessModule />} />

      <Route path="/" element={<Navigate to="/customer" replace />} />
      <Route path="*" element={<Navigate to="/customer" replace />} />
    </Routes>
  );
};

// Extracted styles for cleaner JSX
const loaderStyles = {
  container: {
    display: 'flex',
    height: '100vh',
    width: '100vw',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#f9fafb',
    fontFamily: 'sans-serif'
  },
  content: { textAlign: 'center' },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #e5e7eb',
    borderTopColor: '#2563eb', // Standard Blue
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto'
  },
  text: {
    marginTop: '16px',
    color: '#374151',
    fontWeight: '500'
  }
};

export default PostLoginRedirect;